import { Component, OnInit, OnDestroy, Input, Output, EventEmitter } from '@angular/core';
import { RunView } from '@memberjunction/core';
import { NavigationService } from '@memberjunction/ng-shared';
import { Subject } from 'rxjs';
import { IzzyChannelMessageEntity } from 'mj_generatedentities';
import { BCSaaSChannelEntity, BCSaaSChannelRunEntity } from '@bluecypress/bcsaas-entities';
import { IAdminPanel, AdminPanelStatus, DEFAULT_ADMIN_PANEL_STATUS } from '../admin-panel.interface';

interface RunDisplay {
  run: BCSaaSChannelRunEntity;
  channel: BCSaaSChannelEntity | null;
}

@Component({
  standalone: false,
  selector: 'izzy-channel-runs',
  templateUrl: './channel-runs.component.html',
  styleUrls: ['./channel-runs.component.css']
})
export class ChannelRunsComponent implements OnInit, OnDestroy, IAdminPanel {
  @Input() channelId: string | null = null;
  @Input() initialState: any = null;
  @Output() stateChange = new EventEmitter<any>();
  @Output() statusChange = new EventEmitter<AdminPanelStatus>();
  @Output() interaction = new EventEmitter<any>();

  constructor(private navigationService: NavigationService) {}

  protected runs: BCSaaSChannelRunEntity[] = [];
  protected runDisplays: RunDisplay[] = [];
  protected channels: BCSaaSChannelEntity[] = [];

  protected isLoading: boolean = true;
  protected errorMessage: string = '';
  protected searchQuery: string = '';
  protected statusFilter: string = 'all';
  protected channelFilter: string = 'all';

  protected selectedRun: RunDisplay | null = null;

  // Drill-down state
  protected showDrillDown: boolean = false;
  protected drillDownRun: RunDisplay | null = null;
  protected drillDownMessages: IzzyChannelMessageEntity[] = [];
  protected isDrillDownLoading: boolean = false;

  private destroy$ = new Subject<void>();

  async ngOnInit(): Promise<void> {
    await this.loadData();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  protected async loadData(): Promise<void> {
    this.isLoading = true;
    this.errorMessage = '';

    try {
      // Load channels for reference
      const rvChannels = new RunView();
      const channelsResult = await rvChannels.RunView<BCSaaSChannelEntity>({
        EntityName: 'BC: Channels',
        OrderBy: 'Name ASC'
      });
      this.channels = channelsResult.Results;

      // Load runs
      let extraFilter = '';
      if (this.channelId) {
        extraFilter = `ChannelID = '${this.channelId}'`;
      }
      const rv = new RunView();
      const result = await rv.RunView<BCSaaSChannelRunEntity>({
        EntityName: 'BC: Channel Runs',
        ExtraFilter: extraFilter || undefined,
        OrderBy: 'StartedAt DESC',
        MaxRows: 100
      });
      this.runs = result.Results;
      this.runDisplays = this.runs.map(r => this.createDisplay(r));

      // Emit default status (channel runs don't have a health indicator)
      this.statusChange.emit(DEFAULT_ADMIN_PANEL_STATUS);
    } catch (error) {
      console.error('Error loading channel runs:', error);
      this.errorMessage = 'Failed to load channel runs.';
    } finally {
      this.isLoading = false;
    }
  }

  private createDisplay(run: BCSaaSChannelRunEntity): RunDisplay {
    const channel = this.channels.find(c => c.ID === run.ChannelID) || null;
    return { run, channel };
  }

  get filteredRuns(): RunDisplay[] {
    return this.runDisplays.filter(rd => {
      const matchesSearch = !this.searchQuery.trim() ||
        (rd.channel && rd.channel.Name.toLowerCase().includes(this.searchQuery.toLowerCase()));
      const matchesStatus = this.statusFilter === 'all' || rd.run.Status === this.statusFilter;
      const matchesChannel = this.channelFilter === 'all' || rd.run.ChannelID === this.channelFilter;
      return matchesSearch && matchesStatus && matchesChannel;
    });
  }

  selectRun(rd: RunDisplay): void {
    this.selectedRun = this.selectedRun === rd ? null : rd;
  }

  getStatusClass(status: string): string {
    switch (status) {
      case 'Completed': return 'status-completed';
      case 'Running': return 'status-running';
      case 'Failed': return 'status-failed';
      case 'Pending': return 'status-pending';
      default: return '';
    }
  }

  getStatusIcon(status: string): string {
    switch (status) {
      case 'Completed': return 'fa-check-circle';
      case 'Running': return 'fa-spinner fa-spin';
      case 'Failed': return 'fa-times-circle';
      case 'Pending': return 'fa-clock';
      default: return 'fa-question-circle';
    }
  }

  formatDate(date: Date | string | null): string {
    if (!date) return '-';
    const d = new Date(date);
    return d.toLocaleString();
  }

  formatDuration(start: Date | string | null, end: Date | string | null): string {
    if (!start) return '-';
    const startDate = new Date(start);
    const endDate = end ? new Date(end) : new Date();
    const diffMs = endDate.getTime() - startDate.getTime();
    const diffSec = Math.floor(diffMs / 1000);
    if (diffSec < 60) return `${diffSec}s`;
    const diffMin = Math.floor(diffSec / 60);
    const remainingSec = diffSec % 60;
    if (diffMin < 60) return `${diffMin}m ${remainingSec}s`;
    const diffHour = Math.floor(diffMin / 60);
    const remainingMin = diffMin % 60;
    return `${diffHour}h ${remainingMin}m`;
  }

  getSuccessRate(rd: RunDisplay): number {
    const total = rd.run.MessageCount || 0;
    if (total === 0) return 100;
    const failed = rd.run.FailedCount || 0;
    return Math.round(((total - failed) / total) * 100);
  }

  async refresh(): Promise<void> {
    await this.loadData();
  }

  // ========== Drill-Down Methods ==========

  async openDrillDown(rd: RunDisplay, event: Event): Promise<void> {
    event.stopPropagation(); // Prevent row selection
    this.drillDownRun = rd;
    this.showDrillDown = true;
    this.isDrillDownLoading = true;
    this.drillDownMessages = [];

    try {
      const rv = new RunView();
      const result = await rv.RunView<IzzyChannelMessageEntity>({
        EntityName: 'Channel Messages',
        ExtraFilter: `ChannelRunID = '${rd.run.ID}'`,
        OrderBy: 'ReceivedAt DESC'
      });
      this.drillDownMessages = result.Results;
    } catch (error) {
      console.error('Error loading messages for run:', error);
    } finally {
      this.isDrillDownLoading = false;
    }
  }

  closeDrillDown(): void {
    this.showDrillDown = false;
    this.drillDownRun = null;
    this.drillDownMessages = [];
  }

  navigateToMessage(message: IzzyChannelMessageEntity): void {
    this.closeDrillDown();

    const channel = this.channels.find(c => c.ID === message.ChannelID);
    const queryParams: Record<string, string> = {
      messageId: message.ID,
      approvalStatus: message.ApprovalStatus || 'All',
      generationStatus: message.GenerationStatus || 'All'
    };
    if (channel) {
      queryParams['channelId'] = channel.ID;
      queryParams['organizationId'] = channel.OrganizationID;
    }
    this.navigationService.OpenNavItemByName('Messages', queryParams);
  }

  getStatusColor(status: string): string {
    switch (status?.toLowerCase()) {
      case 'generated':
      case 'completed':
      case 'approved':
        return '#16a34a';
      case 'failed':
      case 'rejected':
        return '#dc2626';
      case 'pending':
      case 'pending approval':
        return '#f59e0b';
      case 'running':
      case 'processing':
        return '#2563eb';
      default:
        return '#6b7280';
    }
  }

  getTimeSince(date: Date | string | null): string {
    if (!date) return 'N/A';
    const d = new Date(date);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffHour = Math.floor(diffMin / 60);
    const diffDay = Math.floor(diffHour / 24);

    if (diffSec < 60) return 'Just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    if (diffHour < 24) return `${diffHour}h ago`;
    if (diffDay < 7) return `${diffDay}d ago`;
    return d.toLocaleDateString();
  }

  truncateText(text: string, maxLength: number): string {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  }

  getChannelName(channelId: string): string {
    const channel = this.channels.find(c => c.ID === channelId);
    return channel?.Name || 'Unknown Channel';
  }
}

export function LoadChannelRunsComponent(): void {}
